import React from 'react';
import { Image, ImageSourcePropType, Pressable, StyleSheet, View } from 'react-native';

import { Text } from '~/components/core';
import { AntDesign } from '~/components/core/Icons';
import { useStyles } from '~/hooks';
import { FONT_SIZE_M } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

type Props = {
    label: string;
    noTranslateLabel?: boolean;
    onPress: () => void;
    // Either a bitmap logo or a rendered SVG node, shown in the leading tile.
    image?: ImageSourcePropType;
    imageNode?: React.ReactNode;
};

/**
 * One selectable payment option.
 *
 * Presented as a card with a chevron rather than the previous hairline-separated text rows, which
 * gave no sign they were tappable and read as a disabled list.
 */
export default function OptionRow({ label, noTranslateLabel, onPress, image, imageNode }: Props) {
    const styles = useStyles(stylesGenerator);
    return (
        <Pressable
            onPress={onPress}
            accessibilityRole="button"
            style={({ pressed }) => [styles.row, pressed ? styles.rowPressed : null]}
        >
            <View style={styles.logoTile}>
                {imageNode ?? (image ? <Image source={image} style={styles.logo} resizeMode="contain" /> : null)}
            </View>
            <Text noTranslate={noTranslateLabel} style={styles.label} numberOfLines={2}>
                {label}
            </Text>
            <AntDesign name="caretright" size={scaleSize(12)} style={styles.chevron} />
        </Pressable>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        row: {
            flexDirection: 'row',
            alignItems: 'center',
            borderRadius: scaleSize(8),
            borderWidth: 1,
            borderColor: theme.border_2,
            backgroundColor: theme.backgroundSecondary,
            paddingHorizontal: scaleSize(12),
            paddingVertical: scaleSize(12),
            marginBottom: scaleSize(10),
        },
        rowPressed: {
            opacity: 0.6,
            borderColor: theme.button,
        },
        logoTile: {
            justifyContent: 'center',
            alignItems: 'center',
            width: scaleSize(52),
            height: scaleSize(32),
            backgroundColor: theme.white,
            borderRadius: scaleSize(4),
            marginRight: scaleSize(12),
            overflow: 'hidden',
        },
        logo: {
            width: scaleSize(44),
            height: scaleSize(24),
        },
        label: {
            flex: 1,
            fontSize: FONT_SIZE_M,
            color: theme.text,
        },
        chevron: {
            color: theme.textSecondary,
            marginLeft: scaleSize(8),
        },
    });
